import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Alert, Snackbar } from '@mui/material';
import { setNotifier, clearNotifier } from '../utils/notificationBridge';

type Severity = 'success' | 'error' | 'warning' | 'info';

interface Notification {
  message: string;
  severity: Severity;
  key: number;
}

interface NotificationContextValue {
  showSuccess: (msg: string) => void;
  showError: (msg: string) => void;
  showWarning: (msg: string) => void;
  showInfo: (msg: string) => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export const useNotification = (): NotificationContextValue => {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return ctx;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [queue, setQueue] = useState<Notification[]>([]);
  const [current, setCurrent] = useState<Notification | null>(null);
  const [open, setOpen] = useState(false);
  const keyRef = useRef(0);

  const enqueue = useCallback((message: string, severity: Severity) => {
    keyRef.current += 1;
    setQueue((prev) => [...prev, { message, severity, key: keyRef.current }]);
  }, []);

  useEffect(() => {
    if (queue.length > 0 && !current) {
      setCurrent(queue[0]);
      setQueue((prev) => prev.slice(1));
      setOpen(true);
    }
  }, [queue, current]);

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  const handleExited = () => {
    setCurrent(null);
  };

  const showSuccess = useCallback((msg: string) => enqueue(msg, 'success'), [enqueue]);
  const showError = useCallback((msg: string) => enqueue(msg, 'error'), [enqueue]);
  const showWarning = useCallback((msg: string) => enqueue(msg, 'warning'), [enqueue]);
  const showInfo = useCallback((msg: string) => enqueue(msg, 'info'), [enqueue]);

  useEffect(() => {
    setNotifier(enqueue);
    return () => clearNotifier();
  }, [enqueue]);

  return (
    <NotificationContext.Provider value={{ showSuccess, showError, showWarning, showInfo }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={current?.severity === 'error' ? 8000 : 5000}
        onClose={handleClose}
        TransitionProps={{ onExited: handleExited }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {current ? (
          <Alert
            onClose={handleClose}
            severity={current.severity}
            variant="filled"
            sx={{ width: '100%', whiteSpace: 'pre-line', maxWidth: 480 }}
          >
            {current.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </NotificationContext.Provider>
  );
};
